"use client";

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  getProductsApi,
  getProductBySlugApi,
  getProductByIdApi,
  getSpecialOffersApi,
  getSpecialOfferBySlugApi,
  getProductsAdminApi,
  createProductApi,
  updateProductAdminByIdApi,
  deleteProductAdminByIdApi,
  bulkUpdateProductsApi,
  getDraftProductsApi,
  getNewArrivalsApi,
  getBestSellerApi,
  searchProductsApi,
  toggleProductStatusApi,
  refreshProductApi,
  saveOptionConfigsApi,
} from "../api/products.api";
import { toast } from "sonner";
import {
  Product,
  ProductResponse,
  ProductsMeta,
  CreateProductPayload,
  UpdateProductPayload,
  SpecialOffer,
  SpecialOfferDetail
} from "@/types/product.types";
import {
  ProductResponseSchema,
  PaginatedProductResponseSchema
} from "@/lib/validations/api.schema";

interface MutationCallbacks<TData, TVars, TCtx = unknown> {
  onSuccess?: (data: TData, variables: TVars, context: TCtx) => void | Promise<unknown>;
  onError?: (error: AxiosError, variables: TVars, context: TCtx) => void | Promise<unknown>;
}

export const productsQueryKeys = {
  all: ["products"] as const,
  lists: () => [...productsQueryKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown> | UseProductsParams) =>
    [...productsQueryKeys.lists(), { filters }] as const,
  adminLists: () => [...productsQueryKeys.all, "admin-list"] as const,
  adminList: (filters?: Record<string, unknown> | UseProductsParams) =>
    [...productsQueryKeys.adminLists(), { filters }] as const,
  drafts: () => [...productsQueryKeys.all, "drafts"] as const,
  details: () => [...productsQueryKeys.all, "detail"] as const,
  detail: (idOrSlug: string) =>
    [...productsQueryKeys.details(), idOrSlug] as const,
  specialOffers: () => [...productsQueryKeys.all, "special-offers"] as const,
  specialOffer: (slug: string) =>
    [...productsQueryKeys.specialOffers(), slug] as const,
  newArrivals: () => [...productsQueryKeys.all, "new-arrivals"] as const,
  bestSellers: () => [...productsQueryKeys.all, "best-sellers"] as const,
  toggleProductStatus: () => [...productsQueryKeys.all, "toggle-status"] as const,
};


interface UseProductsParams {
  page?: number | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
}

// CREATE PRODUCT HOOK
export const useCreateProduct = (
  options?: Partial<UseMutationOptions<ProductResponse, AxiosError, CreateProductPayload, unknown>> & MutationCallbacks<ProductResponse, CreateProductPayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateProductPayload) => {
      const formData = new FormData();
      formData.append("name", payload.name);
      formData.append("description", payload.description);
      if (payload.descriptionHtml) formData.append("descriptionHtml", payload.descriptionHtml);
      if (payload.descriptionJson) formData.append("descriptionJson", payload.descriptionJson);
      formData.append("sku", payload.sku);
      formData.append("price", payload.price.toString());
      formData.append("stock", payload.stock.toString());
      if (payload.subName) formData.append("subName", payload.subName);
      if (payload.size) formData.append("size", payload.size);

      if (payload.mainImage) {
        formData.append("mainImage", payload.mainImage);
      }

      payload.gallery.forEach((file: File) => {
        formData.append("galleryImages", file);
      });

      if (payload.subCategory) {
        formData.append("categoryIds", JSON.stringify([payload.subCategory]));
      } else if (payload.category) {
        formData.append("categoryIds", JSON.stringify([payload.category]));
      }

      if (payload.tags && payload.tags.length > 0) {
        formData.append("tags", JSON.stringify(payload.tags));
      }

      if (payload.attributeIds && payload.attributeIds.length > 0) {
        formData.append("attributeIds", JSON.stringify(payload.attributeIds));
      }

      if (payload.attributeOptionConfigs && payload.attributeOptionConfigs.length > 0) {
        formData.append("attributeOptionConfigs", JSON.stringify(payload.attributeOptionConfigs));
      }

      if (payload.useGlobalAttributes !== undefined) {
        formData.append(
          "useGlobalAttributes",
          payload.useGlobalAttributes.toString()
        );
      }

      if (payload.status) {
        formData.append("status", payload.status);
      }

      if (payload.type) {
        formData.append("type", payload.type);
      }

      if (payload.generateThumbnail !== undefined) {
        formData.append("generateThumbnail", payload.generateThumbnail.toString());
      }

      if (payload.generateSeo !== undefined) {
        formData.append("generateSeo", payload.generateSeo.toString());
      }

      return createProductApi(formData);
    },
    onSuccess: (data, variables, context) => {
      toast.success("Produit créé avec succès ! 🚀");
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as { message?: string })?.message ||
        "Erreur lors de la création du produit."
      );
      console.error("Failed to create product:", error);
      options?.onError?.(error, variables, context);
    },
    ...options,
  });
};

// UPDATE PRODUCT ADMIN HOOK
export const useUpdateProductAdmin = (
  options?: Partial<UseMutationOptions<ProductResponse, AxiosError, UpdateProductPayload, unknown>> & MutationCallbacks<ProductResponse, UpdateProductPayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateProductPayload) => {
      const formData = new FormData();
      if (payload.name) formData.append("name", payload.name);
      if (payload.description) formData.append("description", payload.description);
      if (payload.descriptionHtml) formData.append("descriptionHtml", payload.descriptionHtml);
      if (payload.descriptionJson) formData.append("descriptionJson", payload.descriptionJson);
      if (payload.sku) formData.append("sku", payload.sku);
      if (payload.price !== undefined) formData.append("price", payload.price.toString());
      if (payload.stock !== undefined) formData.append("stock", payload.stock.toString());
      if (payload.subName) formData.append("subName", payload.subName);
      if (payload.size) formData.append("size", payload.size);

      if (payload.mainImage) {
        formData.append("mainImage", payload.mainImage);
      }

      if (payload.gallery) {
        payload.gallery.forEach((file: File) => {
          formData.append("galleryImages", file);
        });
      }

      if (payload.subCategory) {
        formData.append("categoryIds", JSON.stringify([payload.subCategory]));
      } else if (payload.category) {
        formData.append("categoryIds", JSON.stringify([payload.category]));
      }

      if (payload.tags && payload.tags.length > 0) {
        formData.append("tags", JSON.stringify(payload.tags));
      }

      if (payload.attributeIds && payload.attributeIds.length > 0) {
        formData.append("attributeIds", JSON.stringify(payload.attributeIds));
      }

      if (payload.attributeOptionConfigs && payload.attributeOptionConfigs.length > 0) {
        formData.append("attributeOptionConfigs", JSON.stringify(payload.attributeOptionConfigs));
      }

      if (payload.useGlobalAttributes !== undefined) {
        formData.append(
          "useGlobalAttributes",
          payload.useGlobalAttributes.toString()
        );
      }

      if (payload.status) {
        formData.append("status", payload.status);
      }

      if (payload.type) {
        formData.append("type", payload.type);
      }

      if (payload.generateThumbnail !== undefined) {
        formData.append("generateThumbnail", payload.generateThumbnail.toString());
      }

      if (payload.generateSeo !== undefined) {
        formData.append("generateSeo", payload.generateSeo.toString());
      }

      if (
        payload.galleryImageIdsToDelete &&
        payload.galleryImageIdsToDelete.length > 0
      ) {
        formData.append(
          "galleryImageIdsToDelete",
          JSON.stringify(payload.galleryImageIdsToDelete)
        );
      }

      return updateProductAdminByIdApi(payload.id, formData);
    },
    onSuccess: (data, payload, context) => {
      toast.success("Produit mis à jour avec succès ! 🚀");
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(payload.id),
      });
      options?.onSuccess?.(data, payload, context);
    },
    onError: (error, payload, context) => {
      toast.error(
        (error.response?.data as { message?: string })?.message ||
        "Erreur lors de la mise à jour du produit."
      );
      console.error("Failed to update product:", error);
      options?.onError?.(error, payload, context);
    },
    ...options,
  });
};

// SAVE OPTION CONFIGS HOOK (dedicated endpoint — no full product update)
export const useSaveOptionConfigs = (
  productId: string | undefined,
  options?: Partial<UseMutationOptions<{ success: boolean; data: { productId: string; saved: number } }, AxiosError, Array<{ attributeOptionId: string; price: string; isEnabled: boolean }>, unknown>> & MutationCallbacks<{ success: boolean; data: { productId: string; saved: number } }, Array<{ attributeOptionId: string; price: string; isEnabled: boolean }>>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (configs: Array<{ attributeOptionId: string; price: string; isEnabled: boolean }>) => {
      if (!productId) throw new Error('No productId');
      return saveOptionConfigsApi(productId, configs);
    },
    onSuccess: (data) => {
      toast.success('Prix des options sauvegardés !');
      if (productId) {
        queryClient.invalidateQueries({ queryKey: productsQueryKeys.detail(productId) });
      }
      options?.onSuccess?.(data, [], undefined);
    },
    onError: (error) => {
      toast.error(
        (error.response?.data as { message?: string })?.message ||
        'Erreur lors de la sauvegarde des prix.'
      );
    },
    ...options,
  });
};

// DELETE PRODUCT ADMIN HOOK
export const useDeleteProductAdmin = (
  options?: Partial<UseMutationOptions<ProductResponse, AxiosError, { id: string }, unknown>> & MutationCallbacks<ProductResponse, { id: string }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteProductAdminByIdApi(id),
    onSuccess: (data, variables, context) => {
      toast.success("Produit supprimé avec succès ! 🚀");
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as { message?: string })?.message ||
        "Erreur lors de la suppression du produit."
      );
      console.error("Failed to delete product:", error);
      options?.onError?.(error, variables, context);
    },
    ...options,
  });
};

// BULK UPDATE PRODUCTS HOOK
export const useBulkUpdateProducts = (
  options?: Partial<UseMutationOptions<{ success: boolean; message: string }, AxiosError, FormData, unknown>> & MutationCallbacks<{ success: boolean; message: string }, FormData>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) => bulkUpdateProductsApi(formData),
    onSuccess: (data, variables, context) => {
      toast.success("Produits mis à jour avec succès ! 🚀");
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as { message?: string })?.message ||
        "Erreur lors de la mise à jour des produits."
      );
      console.error("Failed to bulk update products:", error);
      options?.onError?.(error, variables, context);
    },
    ...options,
  });
};

// 🔹 LIST HOOK
export const useProducts = (
  params?: UseProductsParams,
  options?: Partial<UseQueryOptions<{ products: Product[]; meta: ProductsMeta }, AxiosError>>
) => {
  return useQuery({
    queryKey: productsQueryKeys.list(params),
    queryFn: async () => {
      const response = await getProductsApi(params);
      const result = PaginatedProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useProducts:", result.error);
        // Return raw data as fallback if structure matches partially, or empty defaults
        const rawData = (response as any)?.data?.data || [];
        const rawMeta = (response as any)?.data?.meta || { total: 0, page: 1, limit: 12, totalPages: 1 };
        return { products: rawData, meta: rawMeta };
      }

      return {
        products: result.data.data.data as Product[],
        meta: result.data.data.meta as ProductsMeta,
      };
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useProductsAdmin = (
  params?: UseProductsParams,
  options?: Partial<UseQueryOptions<{ products: Product[]; meta: ProductsMeta }, AxiosError>>
) => {
  return useQuery({
    queryKey: productsQueryKeys.adminList(params),
    queryFn: async () => {
      const response = await getProductsAdminApi(params);
      const result = PaginatedProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useProductsAdmin:", result.error);
        const rawData = (response as any)?.data?.data || [];
        const rawMeta = (response as any)?.data?.meta || { total: 0, page: 1, limit: 12, totalPages: 1 };
        return { products: rawData, meta: rawMeta };
      }

      return {
        products: result.data.data.data as Product[],
        meta: result.data.data.meta as ProductsMeta,
      };
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

// 🔹 INFINITE LIST HOOK (ADMIN) — powers the read-only Store catalog viewer
export const useInfiniteProductsAdmin = (params?: { limit?: number; sort?: string }) => {
  const limit = params?.limit ?? 24;

  return useInfiniteQuery({
    queryKey: [...productsQueryKeys.adminLists(), "infinite", { limit, sort: params?.sort }],
    queryFn: async ({ pageParam }) => {
      const response = await getProductsAdminApi({ page: pageParam, limit, sort: params?.sort });
      const result = PaginatedProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useInfiniteProductsAdmin:", result.error);
        const rawData = (response as any)?.data?.data || [];
        const rawMeta = (response as any)?.data?.meta || { total: 0, page: pageParam, limit, totalPages: 1 };
        return { products: rawData as Product[], meta: rawMeta as ProductsMeta };
      }

      return {
        products: result.data.data.data as Product[],
        meta: result.data.data.meta as ProductsMeta,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages, hasNextPage } = lastPage.meta;
      if (hasNextPage === false) return undefined;
      if (typeof totalPages === "number" && page >= totalPages) return undefined;
      return page + 1;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useSearchProducts = (
  params: {
    q?: string;
    page?: number;
    limit?: number;
    sort?: string;
    minPrice?: number;
    maxPrice?: number;
    categoryIds?: string[];
    inStock?: boolean;
  },
  options?: Partial<UseQueryOptions<{ products: Product[]; meta: ProductsMeta }, AxiosError>>
) => {
  return useQuery({
    queryKey: [...productsQueryKeys.all, "search", params],
    queryFn: async () => {
      const response = await searchProductsApi(params);
      const result = PaginatedProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useSearchProducts:", result.error);
        const rawData = (response as any)?.data?.data || [];
        const rawMeta = (response as any)?.data?.meta || { total: 0, page: 1, limit: 12, totalPages: 1 };
        return { products: rawData, meta: rawMeta };
      }

      return {
        products: result.data.data.data as Product[],
        meta: result.data.data.meta as ProductsMeta,
      };
    },
    enabled: !!params.q && params.q.trim().length > 1,
    staleTime: 1000 * 60 * 2, // 2 minutes for search
    ...options,
  });
};

export const useDraftProducts = (
  params?: { page?: number; limit?: number },
  options?: Partial<UseQueryOptions<{ products: Product[]; meta: ProductsMeta }, AxiosError>>
) => {
  return useQuery({
    queryKey: [...productsQueryKeys.all, "drafts", params],
    queryFn: async () => {
      const response = await getDraftProductsApi(params);
      const result = PaginatedProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useDraftProducts:", result.error);
        const rawData = (response as any)?.data?.data || [];
        const rawMeta = (response as any)?.data?.meta || { total: 0, page: 1, limit: 12, totalPages: 1 };
        return { products: rawData, meta: rawMeta };
      }

      return {
        products: result.data.data.data as Product[],
        meta: result.data.data.meta as ProductsMeta,
      };
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

// 🔹 SINGLE PRODUCT HOOK (BY SLUG)
export const useProductBySlug = (
  slug: string,
  options?: Partial<UseQueryOptions<Product, AxiosError>>
) => {
  return useQuery({
    queryKey: productsQueryKeys.detail(slug),
    queryFn: async () => {
      const response = await getProductBySlugApi(slug);
      const result = ProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useProductBySlug:", result.error);
        return (response as any).data as Product;
      }

      return result.data.data as Product;
    },
    enabled: !!slug,
    ...options,
  });
};

// 🔹 SINGLE PRODUCT HOOK (BY ID)
export const useProductById = (
  id: string,
  options?: Partial<UseQueryOptions<Product, AxiosError>>
) => {
  return useQuery({
    queryKey: productsQueryKeys.detail(id),
    queryFn: async () => {
      const response = await getProductByIdApi(id);
      const result = ProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useProductById:", result.error);
        return (response as any).data as Product;
      }

      return result.data.data as Product;
    },
    enabled: !!id,
    ...options,
  });
};

// 🔹 SPECIAL OFFERS HOOK (singleton — returns one active offer)
export const useSpecialOffers = (
  options?: Partial<UseQueryOptions<SpecialOffer | null, AxiosError>>
) => {
  return useQuery({
    queryKey: productsQueryKeys.specialOffers(),
    queryFn: async () => {
      const response = await getSpecialOffersApi();
      const data = (response as any).data;
      return data as SpecialOffer | null;
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

// 🔹 SPECIAL OFFER BY SLUG HOOK
export const useSpecialOfferBySlug = (
  slug: string,
  options?: Partial<UseQueryOptions<SpecialOfferDetail, AxiosError>>
) => {
  return useQuery({
    queryKey: productsQueryKeys.specialOffer(slug),
    queryFn: async () => {
      const response = await getSpecialOfferBySlugApi(slug);
      const data = (response as any).message;
      return data as SpecialOfferDetail;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useNewArrivals = (
  params?: { limit?: number },
  options?: Partial<UseQueryOptions<{ products: Product[]; meta: ProductsMeta }, AxiosError>>
) => {
  return useQuery({
    queryKey: [...productsQueryKeys.all, "new-arrivals", params],
    queryFn: async () => {
      const response = await getNewArrivalsApi(params);
      const result = PaginatedProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useNewArrivals:", result.error);
        const rawData = (response as any)?.data?.data || [];
        const rawMeta = (response as any)?.data?.meta || { total: 0, page: 1, limit: 12, totalPages: 1 };
        return { products: rawData, meta: rawMeta };
      }

      return {
        products: result.data.data.data as Product[],
        meta: result.data.data.meta as ProductsMeta,
      };
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useBestSellers = (
  params?: { limit?: number },
  options?: Partial<UseQueryOptions<{ products: Product[]; meta: ProductsMeta }, AxiosError>>
) => {
  return useQuery({
    queryKey: [...productsQueryKeys.all, "best-sellers", params],
    queryFn: async () => {
      const response = await getBestSellerApi(params);
      const result = PaginatedProductResponseSchema.safeParse(response);

      if (!result.success) {
        console.warn("Zod Validation Failed for useNewArrivals:", result.error);
        const rawData = (response as any)?.data?.data || [];
        const rawMeta = (response as any)?.data?.meta || { total: 0, page: 1, limit: 12, totalPages: 1 };
        return { products: rawData, meta: rawMeta };
      }

      return {
        products: result.data.data.data as Product[],
        meta: result.data.data.meta as ProductsMeta,
      };
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useToggleProductStatus = (
  productId: string,
  options?: Partial<UseMutationOptions<ProductResponse, AxiosError, string, unknown>> & MutationCallbacks<ProductResponse, string>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return toggleProductStatusApi(productId);
    },
    onSuccess: (data, variables, context) => {
      toast.success("Statut du produit modifié avec succès ! 🚀");
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as { message?: string })?.message ||
        "Erreur lors de la modification du statut du produit."
      );
      console.error("Failed to toggle product status:", error);
      options?.onError?.(error, variables, context);
    },
    ...options,
  });
};

// REFRESH PRODUCT HOOK
export const useRefreshProduct = (
  productId: string,
  options?: Partial<UseMutationOptions<ProductResponse, AxiosError, void, unknown>> & MutationCallbacks<ProductResponse, void>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return refreshProductApi(productId);
    },
    onSuccess: (data, variables, context) => {
      toast.success("Produit actualisé ! Il réapparaît dans les nouveautés. 🔄");
      queryClient.invalidateQueries({ queryKey: productsQueryKeys.all });
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      toast.error(
        (error.response?.data as { message?: string })?.message ||
        "Erreur lors de l'actualisation du produit."
      );
      options?.onError?.(error, variables, context);
    },
    ...options,
  });
};
